import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'initializers/types';
import { Channel as StreamChannel } from 'stream-chat';

import { MESSENGER_SLICE_NAME } from './constants';
import { View } from './types';

type Sort = 'recent' | 'unread';

const initialState: {
  totalUnreadCount: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  selectedChannel: StreamChannel | null;
  selectedView: View;
  selectedSort: Sort;
  jumpToMessageId: string | null;
} = {
  totalUnreadCount: 0,
  status: 'idle',
  error: null,
  selectedChannel: null,
  selectedView: 'list',
  selectedSort: 'recent',
  jumpToMessageId: null,
};

export const setTotalUnreadCount = createAsyncThunk(
  `${MESSENGER_SLICE_NAME}/setTotalUnreadCount`,
  async (totalUnreadCount: number, { rejectWithValue }) => {
    try {
      return totalUnreadCount;
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred');
    }
  }
);

const slice = createSlice({
  name: MESSENGER_SLICE_NAME,
  initialState,
  reducers: {
    markMessagesAsRead(state, action) {
      state.totalUnreadCount = Math.max(
        0,
        state.totalUnreadCount - action.payload
      );
    },
    setSelectedChannel(state, action) {
      state.selectedChannel = action.payload;
    },
    setSelectedView(state, action) {
      state.selectedView = action.payload;
    },
    setSelectedSort(state, action) {
      state.selectedSort = action.payload;
    },
    setJumpToMessageId(state, action) {
      state.jumpToMessageId = action.payload;
    },
    clearJumpToMessageId(state) {
      state.jumpToMessageId = null;
    },
  },
  extraReducers: builder => {
    builder.addCase(setTotalUnreadCount.fulfilled, (state, action) => {
      state.totalUnreadCount = action.payload;
      state.status = 'succeeded';
    });
    builder.addCase(setTotalUnreadCount.pending, state => {
      state.status = 'loading';
    });
    builder.addCase(setTotalUnreadCount.rejected, (state, action) => {
      state.status = 'failed';
      state.error = action.payload as string;
    });
  },
});

export const selectTotalUnreadCount = (state: RootState) =>
  state.get(MESSENGER_SLICE_NAME).totalUnreadCount;

export const selectSelectedChannel = (state: RootState) =>
  state.get(MESSENGER_SLICE_NAME)?.selectedChannel || null;

export const selectSelectedView = (state: RootState) =>
  state.get(MESSENGER_SLICE_NAME)?.selectedView || 'list';

export const selectSelectedSort = (state: RootState) =>
  state.get(MESSENGER_SLICE_NAME)?.selectedSort || 'recent';

export const selectJumpToMessageId = (state: RootState) =>
  state.get(MESSENGER_SLICE_NAME)?.jumpToMessageId || null;

export const {
  markMessagesAsRead,
  setSelectedChannel,
  setSelectedView,
  setSelectedSort,
  setJumpToMessageId,
  clearJumpToMessageId,
} = slice.actions;

export const { reducer } = slice;
